// Marketplace Photo Gallery Component
// Feature: 006-marketplace-management

import { useState } from 'react';
import { X, ZoomIn, Image, ImageOff } from 'lucide-react';
import { buildImageUrl } from '@/services/api/images';
import type { ItemPhoto } from '@/types/marketplaceAdmin';

interface MarketplacePhotoGalleryProps {
  photos: ItemPhoto[];
  itemTitle: string;
}

export function MarketplacePhotoGallery({
  photos,
  itemTitle,
}: MarketplacePhotoGalleryProps) {
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  if (photos.length === 0) {
    return (
      <div className="flex items-center justify-center aspect-video bg-neutral-100 rounded-lg text-neutral-400">
        <div className="flex flex-col items-center gap-2">
          <Image size={48} />
          <span className="text-sm">No photos</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Photo Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((photo, index) => (
          <button
            key={index}
            onClick={() => setSelectedPhoto(photo.url)}
            className="relative group aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-primary-400 transition-all"
          >
            <img
              src={buildImageUrl(photo.url, 'card')}
              alt={`${itemTitle} - Photo ${index + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
                (e.target as HTMLImageElement).parentElement?.querySelector('.fallback-icon')?.classList.remove('hidden');
              }}
            />
            <div className="fallback-icon hidden absolute inset-0 flex items-center justify-center bg-neutral-100">
              <ImageOff className="text-neutral-400" size={32} />
            </div>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center">
              <ZoomIn className="text-white opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
            </div>
            {index === 0 && (
              <div className="absolute top-2 left-2 bg-primary-600 text-white text-xs px-2 py-1 rounded">
                Primary
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setSelectedPhoto(null)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              setSelectedPhoto(null);
            }
          }}
        >
          <button
            onClick={() => setSelectedPhoto(null)}
            className="absolute top-4 right-4 text-white hover:text-neutral-300 transition"
            aria-label="Close"
          >
            <X size={32} />
          </button>
          <img
            src={buildImageUrl(selectedPhoto, 'large')}
            alt={itemTitle}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}
